import { useProjectParams } from '@hooks/useProjectParams';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledSelectField, ControlledTextField, ControlNumberField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { Toolbar } from 'components/Toolbar';
import { UploadStatus } from 'components/UploadStatus';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { ProjectContractTypeEnum, ProjectMethodologyEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useIsUniqProjectName } from 'hooks/useIsUniqProjectName';
import { getContractTypeLabel } from 'types/Contract';
import { methodologyMap } from 'types/methodology';
import { Button, cnMixSpace, Grid, GridItem } from 'uikit';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useIsUniqueProjectCode } from '../hooks/useIsUniqueProjectCode';

import { useStatusChangeDialogMessage } from './hooks/useStatusChangeDialogMessage';
import type { IProjectData } from './ProjectUpdate';
import { updateProjectGql } from './projectUpdateQueries';

interface ProjectUpdateFormProps {
    statusProject: ProjectStatusEnum;
    defaultValues?: IProjectData | null;
}

const requestId = 'updateProjects';
const projectCodeKey = 'code';

export const ProjectUpdateForm = ({ statusProject, defaultValues }: ProjectUpdateFormProps) => {
    const { methodology } = useProjectParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { project: projectRules } = useAccess();
    const { control, handleSubmit, setValue, setError, clearErrors, formState, resetField } = useForm({
        criteriaMode: 'all',
        defaultValues: defaultValues as any,
    });
    const { isDirty, errors } = formState;

    const [isOpenStatusDialog, setIsOpenStatusDialog] = useState<boolean>(false);
    const [isOpenResetStatus, setIsOpenResetStatus] = useState<boolean>(false);

    const isHasContract = !!defaultValues?.contractNavigation?.data?.contractNavData?.hasContract;

    const projectName = useWatch({ control, name: 'name' });
    const uerProjectCode = useWatch({ control, name: projectCodeKey });
    const status = useWatch({ control, name: 'status' });

    const statusDialogWarningText = useStatusChangeDialogMessage(status?.id);

    const { dataIsUniqProjectName, statusIsUniqProjectName, makeResetIsUniqProjectName } =
        useIsUniqProjectName(projectName);

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;
    const isProjectStatusCalculated = statusProject === ProjectStatusEnum.Calculated;

    const isDisabledField = isProjectStatusDelayed || isProjectStatusCompleted || isProjectStatusCalculated;

    useEffect(() => {
        if (formState.dirtyFields.status) {
            setIsOpenStatusDialog(true);
        }
    }, [formState.dirtyFields.status]);

    const onSubmitStatusDialog = useCallback(() => {
        setIsOpenStatusDialog(false);

        if (!isHasContract && status?.id === ProjectStatusEnum.Completed) {
            setIsOpenResetStatus(true);
        }
    }, [isHasContract, status?.id]);

    const onCancelStatusDialog = useCallback(() => {
        resetField('status');
        setIsOpenStatusDialog(false);
    }, [resetField]);

    const onCloseResetStatus = useCallback(() => {
        resetField('status');
        setIsOpenResetStatus(false);
    }, [resetField]);

    useEffect(() => {
        if (
            projectName &&
            projectName !== defaultValues?.name &&
            statusIsUniqProjectName === RequestStatus.SUCCESS &&
            !dataIsUniqProjectName?.result
        ) {
            setError('name', { message: 'Проект с таким именем уже существует' });
        } else {
            clearErrors('name');
        }
    }, [
        clearErrors,
        dataIsUniqProjectName?.result,
        defaultValues?.name,
        projectName,
        setError,
        statusIsUniqProjectName,
    ]);

    const { isUniqueProjectCode } = useIsUniqueProjectCode({
        projectCode: uerProjectCode,
        isProjectCodeDirty: formState?.dirtyFields?.code,
        uniqueId: defaultValues?.uniqueId,
    });

    useEffect(() => {
        if (uerProjectCode && !isUniqueProjectCode) {
            setError(projectCodeKey, { message: 'Проект с таким кодом уже существует' });
        } else {
            clearErrors(projectCodeKey);
        }
    }, [clearErrors, isUniqueProjectCode, setError, uerProjectCode]);

    useEffect(
        () => () => {
            makeResetIsUniqProjectName(null);
        },
        [makeResetIsUniqProjectName],
    );

    const [updateProjectData, updateProjectStatus, updateProjectError, updateProject, setUpdateProject] = useRequest({
        requestId,
        query: updateProjectGql,
        withEffect: false,
    });

    // TODO: ориентируемся на сообщение об ошибке.
    useEffect(() => {
        if (
            !updateProjectData?.updateProject?.result &&
            updateProjectData?.updateProject?.message ===
                'В проекте имеются незакрытые объемы. Завершение проекта невозможно'
        ) {
            setValue('status', defaultValues?.status);
        }
    }, [defaultValues?.status, dispatch, setUpdateProject, setValue, updateProjectData?.updateProject]);

    const [isSubmitted, setIsSubmitted] = useState(false);

    const onSubmit = useCallback(
        (data) => {
            setIsSubmitted(true);

            dispatch(
                updateProject({
                    uniqueId: data.uniqueId,
                    name: data.name,
                    stage: data.stage?.id,
                    status: data.status?.id,
                    contractType: data.contractType?.id,
                    ...(data?.code && { code: data?.code }),
                }),
            );
        },
        [dispatch, updateProject],
    );

    const formRef = useRef<HTMLFormElement>(null);

    const handleSave = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, [formRef]);

    const handleCancel = useCallback(() => {
        navigate(`/projects/${methodology}/${defaultValues?.uniqueId}`);
    }, [defaultValues?.uniqueId, methodology, navigate]);

    const methodologySelectItems = useMemo(
        () => [
            { label: 'УЕР', id: 'UER' },
            { label: 'БИМ', id: 'BIM' },
            { label: 'ЕТР', id: 'ETR' },
        ],
        [],
    );

    const contractTypeSelectItems = useMemo(
        () => [
            { id: ProjectContractTypeEnum.Frame, label: getContractTypeLabel(ProjectContractTypeEnum.Frame) },
            { id: ProjectContractTypeEnum.Normal, label: getContractTypeLabel(ProjectContractTypeEnum.Normal) },
        ],
        [],
    );

    const statusSelectItems = useMemo(
        () => [...(defaultValues?.statusFollowers || []), ...(defaultValues?.status ? [defaultValues?.status] : [])],
        [defaultValues],
    );

    const stageSelectItems = useMemo(
        () => [...(defaultValues?.stage ? [defaultValues?.stage] : [])],
        [defaultValues?.stage],
    );

    useEffect(() => {
        if (isSubmitted && updateProjectData?.updateProject?.result && updateProjectStatus === RequestStatus.SUCCESS) {
            dispatch(setUpdateProject(undefined));
            navigate(`/projects/${methodology}/${defaultValues?.uniqueId}`, { state: { refreshProject: true } });
            setIsSubmitted(false);
        }
    }, [
        defaultValues?.uniqueId,
        dispatch,
        isSubmitted,
        methodology,
        navigate,
        setUpdateProject,
        updateProjectData?.updateProject?.result,
        updateProjectStatus,
    ]);

    const handleCreateReportUpdateProject = () => {
        ExportCSVReport(updateProjectData?.updateProject?.validation);
    };

    const handleCloseModalErrorUpdateProject = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const isBimProject = useMemo(() => methodology === methodologyMap[ProjectMethodologyEnum.Bim], [methodology]);

    const isSaveDisabled = !projectRules.canSave || !isDirty || !projectName?.length || !!Object.keys(errors).length;

    return (
        <>
            <Toolbar fullLine>
                <Button
                    label="Сохранить"
                    onClick={handleSave}
                    loading={updateProjectStatus === RequestStatus.FETCHING}
                    disabled={isSaveDisabled}
                    size="s"
                />
                <Button
                    label="Отменить"
                    view="ghost"
                    onClick={handleCancel}
                    disabled={updateProjectStatus === RequestStatus.FETCHING}
                    className={cnMixSpace({ mL: 's' })}
                    size="s"
                />
            </Toolbar>
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef} style={{ width: 640 }}>
                <h3 style={{ fontWeight: 400 }}>Основная информация</h3>
                <Grid rowGap="xs" cols="1">
                    <GridItem>
                        <ControlledTextField
                            name="name"
                            label="Наименование"
                            control={control}
                            type="text"
                            placeholder="Наименование проекта"
                            required
                            rules={{
                                maxLength: {
                                    value: 255,
                                    message: 'не более 255 символов',
                                },
                            }}
                            disabled={isDisabledField}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="methodology"
                            label="Методология расчета"
                            control={control}
                            placeholder="Методология расчета"
                            required
                            items={methodologySelectItems}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="stage"
                            label="Этап"
                            control={control}
                            placeholder="Этап"
                            required
                            items={stageSelectItems}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="status"
                            label="Статус"
                            control={control}
                            placeholder="Статус"
                            required
                            items={statusSelectItems}
                            disabled={isProjectStatusCompleted}
                        />
                    </GridItem>

                    <GridItem>
                        <ControlledTextField
                            name="code"
                            label="Код проекта"
                            control={control}
                            rules={{
                                maxLength: {
                                    value: 255,
                                    message: 'не более 255 символов',
                                },
                            }}
                        />
                    </GridItem>

                    <GridItem>
                        <ControlledTextField
                            name="createdAt"
                            label="Дата создания"
                            control={control}
                            type="text"
                            placeholder="Дата создания"
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="authoredBy"
                            label="Автор"
                            control={control}
                            type="text"
                            placeholder="Автор"
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="subsidiary.label"
                            label="Дочернее общество"
                            control={control}
                            type="text"
                            placeholder="Дочернее общество"
                            disabled
                        />
                    </GridItem>

                    {isBimProject && (
                        <GridItem>
                            <ControlledSelectField
                                name="contractType"
                                label="Тип договора"
                                control={control}
                                placeholder="Тип договора"
                                required
                                items={contractTypeSelectItems}
                                disabled={isDisabledField}
                            />
                        </GridItem>
                    )}
                </Grid>

                <h3 style={{ fontWeight: 400 }}>Цена проекта</h3>
                <Grid rowGap="xs" cols="1">
                    {defaultValues?.contractPrice && (
                        <>
                            <GridItem>
                                <ControlNumberField
                                    name="lotPrice"
                                    label="Цена договора без НДС (руб.)"
                                    labelPosition="left"
                                    width="full"
                                    size="s"
                                    type="number"
                                    placeholder="Цена договора"
                                    control={control}
                                    disabled
                                />
                            </GridItem>
                            {defaultValues?.contractPrice?.data?.hasActualCost && (
                                <GridItem>
                                    <ControlNumberField
                                        name="actualPrice"
                                        label="Цена фактическая без НДС (руб.)"
                                        labelPosition="left"
                                        width="full"
                                        size="s"
                                        type="number"
                                        placeholder="Цена фактическая"
                                        control={control}
                                        disabled
                                    />
                                </GridItem>
                            )}
                        </>
                    )}
                    {defaultValues?.calculationPrice && (
                        <GridItem>
                            <ControlNumberField
                                name="priceNMC"
                                label="Цена расчета НМЦ (руб.)"
                                labelPosition="left"
                                width="full"
                                size="s"
                                type="number"
                                placeholder="Цена расчета НМЦ"
                                control={control}
                                disabled
                            />
                        </GridItem>
                    )}
                </Grid>
            </form>
            <ErrorHandlingModal
                titleText="Изменение статуса"
                isSubmitted={isSubmitted}
                statusRequest={updateProjectStatus}
                dataRequest={updateProjectData?.updateProject}
                errorsRequest={updateProjectError}
                handleCloseModalError={handleCloseModalErrorUpdateProject}
                handleCreateReport={handleCreateReportUpdateProject}
            />
            <ModalDialog
                title="Изменение статуса"
                isOpen={isOpenStatusDialog}
                onConfirm={onSubmitStatusDialog}
                onClose={onCancelStatusDialog}
                btnConfirmLabel="Изменить"
                btnCancelLabel="Не изменять"
                withTitleCloseButton
                style={{ width: 520 }}
                bodyMinHeight="unset"
                buttonSize="m"
            >
                <div style={{ padding: 20, lineHeight: '24px' }}>{statusDialogWarningText}</div>
            </ModalDialog>
            <UploadStatus
                isOpened={isOpenResetStatus}
                handleCloseModal={onCloseResetStatus}
                data={{ message: 'Не завершена работа с Договором. Перевод в статус "Завершен" невозможен' }}
                titleText="Изменение статуса"
                style={{ maxWidth: '200px' }}
            >
                <Button label="Понятно" onClick={onCloseResetStatus} />
            </UploadStatus>
        </>
    );
};

ProjectUpdateForm.displayName = 'ProjectUpdateForm';
